import { datadogRum } from '@datadog/browser-rum'
import { Table, TableBody, TableContainer, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { C1Button } from 'c1ui'
import { EmptyList } from 'components/core/empty-list'
import { APIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1TableBody } from 'components/core/list/C1Table/C1TableRedux'
import { C1TableReduxStyles } from 'components/core/list/C1Table/C1TableReduxStyles'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { handleLogout } from 'lib/oauth'
import { useOAuth } from 'lib/rpc'
import { CircleAlert, RefreshCw } from 'lucide-react'
import { FallbackProps } from 'react-error-boundary'
import { FormattedMessage } from 'react-intl'

export const AuthErrorFallback: React.FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
    const oauth = useOAuth()
    const alert = <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
    }}
    >
        <EmptyList
            icon={<C1Icon icon={CircleAlert} />}
            title={<FormattedMessage id="component_c1_table_error" defaultMessage="Something went wrong." />}
            text={<Typography variant="monospace">{error?.message}</Typography>}
            isError
        />
    </Box>

    switch (error.code) {
        // Not Authed
        // code: 16, message: "invalid authorization: Bad authorization header value
        case 16:
            handleLogout(oauth)
            resetErrorBoundary()
            return alert
        default:
            datadogRum.addError(error)
            return alert
    }
}

export const PageErrorFallback: React.FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
    return <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
    }}
    >
        <Box>
            <EmptyList
                icon={<C1Icon icon={CircleAlert} />}
                title={<FormattedMessage id="component_c1_table_error" defaultMessage="Something went wrong." />}
                text={<Typography variant="monospace">{error?.message}</Typography>}
                isError
            />
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <C1Button startIcon={<C1Icon icon={RefreshCw} />} onClick={(): void => resetErrorBoundary()} color="info">
                    <FormattedMessage id="component_c1_table_error" defaultMessage="Try again" />
                </C1Button>
            </Box>
        </Box>
    </Box>
}

export const TableErrorFallback: React.FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
    return <Box>
        <TableContainer
            sx={sxc(C1TableReduxStyles.tableContainer, { borderColor: 'error.light' })}
        >
            <Table size="small" sx={sxc(C1TableReduxStyles.table)}>
                <TableBody>
                    <C1TableBody
                        rows={[]}
                        getId={(): string => ''}
                        staticExtraProps={{}}
                        isRowError={(): boolean => false}
                        emptyState={<EmptyList />}
                        cols={[]}
                        isError
                        isLoading={false}
                    />
                </TableBody>
            </Table>
        </TableContainer>
        <Box sx={{ borderRadius: 1, borderTopLeftRadius: 0, borderTopRightRadius: 0, border: 1, borderColor: 'error.light', borderTop: 0 }}>
            <APIError
                message={error?.message}
                code={error?.code}
                sx={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
                action={<C1Button startIcon={<C1Icon icon={RefreshCw} />} onClick={(): void => resetErrorBoundary()} color="error">
                    <FormattedMessage id="component_c1_table_error" defaultMessage="Try again" />
                </C1Button>}
            />
        </Box>
    </Box>
}
